import { useEffect } from "react";
import PageMeta from "@/components/PageMeta";
import SiteFooter from "@/components/SiteFooter";
import CycleGauge from "@/components/cycle/CycleGauge";
import CycleStats from "@/components/cycle/CycleStats";
import { useInView } from "@/hooks/useInView";
import { CRYPTO_PLAYBOOK, PHASES, TIMELINE } from "@/lib/cycleData";
import { cn } from "@/lib/utils";
import { RefreshCw, Landmark } from "lucide-react";

const TEAL = "var(--cycle-teal-hex)";
const CORAL = "var(--cycle-coral-hex)";

function Section({ children, className }: { children: React.ReactNode; className?: string }) {
  const { ref, inView } = useInView<HTMLElement>(0.1);
  return (
    <section
      ref={ref}
      className={cn("mb-14", inView ? "cycle-fade-up" : "opacity-0", className)}
    >
      {children}
    </section>
  );
}

export default function Cycle() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="The Seven-Year Cycle | GoToCoins"
        description="Where are we in the 7-year economic cycle? A data-driven guide to positioning your business and crypto portfolio for 2028-2030."
        path="/cycle"
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "The Seven-Year Rhythm",
          description:
            "Why business — and Bitcoin — breaks on schedule. A data-driven guide to the 7-year economic cycle and positioning for 2028-2030.",
          author: { "@type": "Person", name: "Sean Grobbelaar" },
          publisher: { "@type": "Organization", name: "GoToCoins" },
          mainEntityOfPage: "https://gotocoin.au/cycle",
        }}
      />

      <main className="container mx-auto px-4 py-12">
        <div className="mx-auto" style={{ maxWidth: 720 }}>
          {/* Title block */}
          <header className="text-center mb-12">
            <p className="font-plex text-[11px] uppercase mb-3" style={{ letterSpacing: "2.5px", color: TEAL }}>
              The Future CEO × GoToCoins
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
              The Seven-Year Rhythm
            </h1>
            <p className="text-lg text-muted-foreground mt-3">
              Why business — and Bitcoin — breaks on schedule
            </p>
            <div className="mx-auto mt-6 h-px w-[60px]" style={{ backgroundColor: TEAL }} />
          </header>

          {/* Section 1 — Gauge */}
          <Section>
            <CycleGauge size={400} className="max-w-[300px] md:max-w-[400px]" />
            <CycleStats className="mt-6" />

            <div
              className="mt-6 rounded-r-lg bg-card px-5 py-4"
              style={{ borderLeft: `3px solid ${CORAL}` }}
            >
              <p className="font-plex text-[11px] uppercase mb-1" style={{ letterSpacing: "2px", color: CORAL }}>
                Next correction window
              </p>
              <p className="text-lg font-bold text-foreground">September 2028 – December 2029</p>
              <p className="text-xs text-muted-foreground mt-1">
                Shemitah year + Juglar cycle peak + Fed rate cycle convergence
              </p>
            </div>
          </Section>

          {/* Section 2 — Historical pattern */}
          <Section>
            <h2 className="text-[22px] font-bold text-foreground mb-6">The pattern</h2>
            <ol className="relative pl-6">
              <span
                className="absolute left-[3px] top-2 bottom-2 border-l border-dashed"
                style={{ borderColor: "hsl(var(--border))" }}
                aria-hidden
              />
              {TIMELINE.map((t, i) => {
                const color = t.next ? TEAL : CORAL;
                return (
                  <li key={t.year} className="relative mb-6 flex items-start gap-3">
                    <span
                      className="absolute -left-6 top-1.5 h-2 w-2 rounded-full cycle-dot-seq"
                      style={{ backgroundColor: color, animationDelay: `${i * 100}ms` }}
                    />
                    <div className="flex-1">
                      <div className="font-plex text-sm font-bold" style={{ color }}>
                        {t.year}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{t.text}</p>
                    </div>
                    <span className="font-plex text-[11px] text-muted-foreground bg-card border border-border/60 rounded-full px-2 py-0.5 shrink-0">
                      {t.next ? "Next" : "+7yr"}
                    </span>
                  </li>
                );
              })}
            </ol>
          </Section>

          {/* Section 3 — Why it happens */}
          <Section>
            <h2 className="text-[22px] font-bold text-foreground mb-6">Why it happens</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl bg-card border border-border/60 p-5">
                <RefreshCw className="h-5 w-5 mb-3" style={{ color: TEAL }} />
                <h3 className="text-base font-bold text-foreground mb-2">The capital investment cycle</h3>
                <p className="text-sm text-muted-foreground leading-[1.7]">
                  In 1862, Clément Juglar identified a 7–11 year economic cycle driven by fixed capital
                  investment. Businesses invest during expansion, creating overcapacity. Returns shrink.
                  Investment collapses. Capital depreciates over roughly seven years — then businesses
                  reinvest. The cycle restarts.
                </p>
              </div>
              <div className="rounded-xl bg-card border border-border/60 p-5">
                <Landmark className="h-5 w-5 mb-3" style={{ color: TEAL }} />
                <h3 className="text-base font-bold text-foreground mb-2">The central bank amplifier</h3>
                <p className="text-sm text-muted-foreground leading-[1.7]">
                  After every crisis, central banks cut rates. Cheap money fuels the next boom. Confidence
                  overheats. Inflation arrives. Rates rise — and the tightening breaks something. Since 1965,
                  eight of twelve Fed hiking cycles have ended in recession. The full loop runs roughly seven
                  years.
                </p>
              </div>
            </div>
          </Section>

          {/* Section 4 — Shemitah */}
          <Section>
            <h2 className="text-[22px] font-bold text-foreground mb-4">The oldest business lesson</h2>
            <p className="text-sm text-muted-foreground leading-[1.7]" style={{ maxWidth: 640 }}>
              Three thousand years ago, the Hebrew Torah prescribed the Shemitah — a mandatory economic reset
              every seventh year. Debts forgiven. Fields left fallow. The economy exhaled. The Shemitah
              calendar tracks against modern financial history with striking precision. The Shemitah years of
              1901, 1916, 1930, 1937, 1966, 1973, 1980, 1987, 2001, 2008, and 2022 all correspond to major
              crashes or recessions.
            </p>
            <div className="mt-5 rounded-r-lg bg-card px-5 py-4" style={{ borderLeft: `3px solid ${TEAL}` }}>
              <p className="text-base font-bold text-foreground">
                The next Shemitah year begins September 2028.
              </p>
            </div>
          </Section>

          {/* Section 5 — Four phases */}
          <Section>
            <h2 className="text-[22px] font-bold text-foreground mb-1">The seven-year playbook</h2>
            <p className="text-sm text-muted-foreground mb-6">
              A quarter-by-quarter guide from here to 2030
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PHASES.map((p, i) => (
                <PhaseCardView key={p.name} phase={p} index={i} />
              ))}
            </div>
          </Section>

          {/* Section 6 — Bitcoin thesis */}
          <Section>
            <h2 className="text-[22px] font-bold text-foreground mb-1">
              The Bitcoin four-year cycle is dead
            </h2>
            <p className="text-sm text-muted-foreground mb-4">Bitcoin has graduated to the macro cycle</p>
            <p className="text-sm text-muted-foreground leading-[1.7]">
              The 2024 halving happened. The expected parabolic bull run didn't. 2025 was the first red
              post-halving year in Bitcoin's history. No exchange collapsed. No stablecoin lost its peg.
              Bitcoin crashed because the macro cycle told it to — the same way stocks, bonds, and real
              estate did.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="rounded-r-lg bg-card px-5 py-4" style={{ borderLeft: `3px solid ${CORAL}` }}>
                <h3 className="font-plex text-xs font-bold uppercase mb-3" style={{ color: CORAL, letterSpacing: "1.5px" }}>
                  The old map (4-year)
                </h3>
                <ul className="text-[13px] text-muted-foreground space-y-1.5">
                  <li className="line-through">Peak in late 2025 ✗</li>
                  <li>Crash through 2026</li>
                  <li>Bottom in 2026–2027</li>
                  <li>Rebuild into 2028 halving</li>
                </ul>
              </div>
              <div className="rounded-r-lg bg-card px-5 py-4" style={{ borderLeft: `3px solid ${TEAL}` }}>
                <h3 className="font-plex text-xs font-bold uppercase mb-3" style={{ color: TEAL, letterSpacing: "1.5px" }}>
                  The new map (7-year)
                </h3>
                <ul className="text-[13px] text-muted-foreground space-y-1.5">
                  <li>Mid-cycle correction 2025–2026</li>
                  <li>Recovery through 2027</li>
                  <li>Exit by September 2028</li>
                  <li>Bottom in 2029</li>
                  <li>Generational entry: early 2030</li>
                </ul>
              </div>
            </div>

            <div className="mt-5 rounded-r-lg bg-card px-5 py-4" style={{ borderLeft: `3px solid ${TEAL}` }}>
              <p className="text-sm text-foreground leading-[1.7]">
                In 2028, these two maps give opposite signals. The four-year crowd will be fully positioned
                for a post-halving moon. The seven-year playbook says that's when you should be lightest.
              </p>
            </div>
          </Section>

          {/* Section 7 — Crypto playbook */}
          <Section>
            <h2 className="text-[22px] font-bold text-foreground mb-6">Seven-year crypto playbook</h2>
            <div className="space-y-4">
              {CRYPTO_PLAYBOOK.map((step, i) => (
                <div
                  key={step.name}
                  className="rounded-xl bg-card border border-border/60 p-5"
                  style={{ borderTop: `3px solid ${step.colorVar}` }}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                    <span
                      className="font-plex text-xs font-bold uppercase"
                      style={{ color: step.colorVar, letterSpacing: "1.5px" }}
                    >
                      {i + 1}. {step.name}
                    </span>
                    <span className="font-plex text-[11px] text-muted-foreground">{step.dates}</span>
                  </div>
                  <ul className="text-[13px] text-muted-foreground space-y-1.5 list-disc pl-4">
                    {step.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          {/* Section 8 — Attribution */}
          <div className="h-px w-full mb-4" style={{ backgroundColor: TEAL, opacity: 0.5 }} />
          <p className="text-xs text-muted-foreground leading-relaxed">
            This analysis is from The Future CEO series by Sean Grobbelaar.{" "}
            <a
              href="https://www.linkedin.com/in/seangrobbelaar/recent-activity/articles/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ color: TEAL }}
            >
              Read the full three-part series on LinkedIn.
            </a>
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function PhaseCardView({ phase, index }: { phase: (typeof PHASES)[number]; index: number }) {
  const { ref, inView } = useInView<HTMLDivElement>(0.1);
  return (
    <div
      ref={ref}
      className={cn(
        "relative rounded-xl bg-card border border-border/60 p-5",
        inView ? "cycle-fade-up" : "opacity-0"
      )}
      style={{
        borderTop: `3px solid ${phase.colorVar}`,
        animationDelay: `${index * 150}ms`,
        boxShadow: phase.current ? `0 0 0 1px ${phase.colorVar}, 0 0 24px -8px ${phase.colorVar}` : undefined,
      }}
    >
      {phase.current && (
        <span
          className="absolute top-3 right-3 font-plex text-[9px] font-bold uppercase px-2 py-1 rounded-full border"
          style={{ color: phase.colorVar, borderColor: phase.colorVar, letterSpacing: "1px" }}
        >
          You are here
        </span>
      )}
      <div className="font-plex text-xs font-bold uppercase" style={{ color: phase.colorVar, letterSpacing: "1.5px" }}>
        {phase.name}
      </div>
      <div className="text-[13px] text-muted-foreground mt-1">{phase.dates}</div>
      <div className="font-plex text-[11px] text-muted-foreground/80 mt-0.5">{phase.years}</div>
      <ul className="mt-4 space-y-2.5">
        {phase.actions.map((a) => (
          <li key={a.label} className="text-[13px] text-muted-foreground leading-[1.6]">
            <span className="font-plex text-[11px] font-bold mr-2" style={{ color: phase.colorVar }}>
              {a.label}
            </span>
            {a.text}
          </li>
        ))}
      </ul>
    </div>
  );
}
